import { cn } from '@/lib/utils';

import Badge from '@/components/atoms/badge';
import LinkButton from '@/components/atoms/linkButton';

import {
  cardTitleStyles,
  ctaContainerStyles,
  footnoteStyles,
  priceWrapperStyles,
  pricingCardContainerStyles,
} from './styles';

import { isDev } from '@/constant/env';
import { useTranslation } from '@/lib/i18n/i18n.client';
import IconMoneyBack from '~/svg/IconMoneyBack.svg';

export type PricingCardProps = {
  id: number;
  title: string;
  discount?: string;
  discountLabel?: string;
  price: number;
  priceOld?: number;
  pricePer: 'IP' | 'GB';
  priceTotal?: number;
  priceTotalOld?: number;
  priceTotalPrefixText?: string;
  priceTotalPostfixText?: string;
  buttonText: string;
  buttonVariant?: 'filled' | 'outlined';
  url: string;
  outlineColor?: 'grey' | 'black';
  outlineText?: string;
  icon?: React.ReactNode;
  showMoneyBack?: boolean;
  alternativeFootnote?: string;
};

export default function PricingCard({
  title,
  discount,
  discountLabel,
  price,
  priceOld,
  pricePer,
  priceTotal,
  priceTotalPrefixText,
  priceTotalPostfixText,
  priceTotalOld,
  buttonText,
  buttonVariant,
  url,
  outlineColor,
  outlineText,
  icon,
  showMoneyBack,
  alternativeFootnote,
}: PricingCardProps) {
  const { texts } = useTranslation();

  const planId = url?.split('planId=')[1] || '';
  const trackId = `${title}-${planId}`;

  const formattedPricePer = pricePer.replaceAll('-', ' ').replaceAll('ww', '');
  const outlineBorderClasses = {
    grey: '',
    black: 'border-neutral-N99',
  } as const;
  const outlineClasses = {
    grey: 'bg-neutral-N20 text-neutral-N99',
    black: 'bg-neutral-N99 text-neutral-N00',
  } as const;

  const transformNumberToWithOneDecimal = (number: number) => {
    const numberString = number.toString();
    if (numberString.includes('.')) {
      return numberString;
    }
    if (numberString === '0') {
      return numberString;
    }
    return number.toFixed(1);
  };

  const Footnote = () => {
    if (showMoneyBack) {
      if (!!alternativeFootnote && isDev) {
        console.warn(
          `Both 'showMoneyBack' and 'alternativeFootnote' props are set for PriceCard - "${title}". Only "showMoneyBack" will be displayed. Please check the card's config in CMS.`
        );
      }

      return (
        <>
          <IconMoneyBack /> <p>{texts.pricing_cards.money_back.money_back}</p>
        </>
      );
    }

    if (!!alternativeFootnote) {
      return <p className='max-w-full text-nowrap'>{alternativeFootnote}</p>;
    }

    return null;
  };

  return (
    <div
      data-tracker-component='card'
      className={cn(
        pricingCardContainerStyles,
        outlineColor && outlineBorderClasses[outlineColor],
        outlineText && 'mt-8'
      )}
    >
      {outlineText && (
        <span
          className={cn(
            'bg-neutral-N99 text-body-l text-neutral-N00 absolute -top-[34px] -z-[1] flex h-[40px] w-[300px] items-center justify-center rounded-t-xl pb-1 uppercase tracking-[0.24em]',
            outlineColor && outlineClasses[outlineColor]
          )}
        >
          {outlineText}
        </span>
      )}

      <p className={cardTitleStyles}>{title}</p>

      <div className={cn('mb-6 h-[22px]', !discount && 'invisible')}>
        <Badge colors='info-I99' variant='filled'>
          {discount && (
            <>
              {discountLabel} {discount}
            </>
          )}
        </Badge>
      </div>

      <div className={priceWrapperStyles}>
        {!!Number(priceOld) && (
          <p className='text-error-E99 text-body-m line-through'>${priceOld}</p>
        )}

        {/* TODO probably better to use PricePer component in here */}
        <p>
          <span className='text-h1'>
            ${transformNumberToWithOneDecimal(price)}
          </span>
          <span className='text-neutral-N50 text-body-m ml-1'>
            / {formattedPricePer}
          </span>
        </p>
      </div>

      <p className='text-neutral-N50 text-body-s mb-6 flex items-center justify-center gap-1'>
        <span>{priceTotalPrefixText}</span>
        {!!Number(priceTotalOld) && (
          <span className='text-body-s mx-0.5 line-through'>
            ${priceTotalOld}
          </span>
        )}
        {priceTotal && (
          <span>
            ${priceTotal} {priceTotalPostfixText}
          </span>
        )}
      </p>

      <div className={ctaContainerStyles}>
        <LinkButton
          data-tracker-context={trackId}
          href={url}
          variant={buttonVariant || 'filled'}
          color='primary'
          slim={false}
          fullwidth
          openInNewTab={false}
          rightSection={
            texts.pricing_cards.contact_sales !== buttonText && icon
          }
          leftSection={texts.pricing_cards.contact_sales === buttonText && icon}
          classNames={{
            root: 'group',
            inner: 'flex items-center justify-center gap-4',
            rightSection:
              'group-hover:translate-x-2 duration-300 ease-in-out transition',
          }}
        >
          {buttonText}
        </LinkButton>
      </div>
      <div className={footnoteStyles}>
        <Footnote />
      </div>
    </div>
  );
}
